import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bell,
  FileText,
  Plus,
  Users,
  FolderOpen,
  Activity,
  Clock,
  BarChart2,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import "./Dashboard.css";
import api from "../../../services/api";

const Dashboard = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loadingApprovals, setLoadingApprovalRequests] = useState(true);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectStats, setProjectStats] = useState({
    active: 0,
    completed: 0,
    unassigned: 0,
    total: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPendingApprovals = async () => {
      try {
        setLoadingApprovalRequests(true);
        const userData = JSON.parse(localStorage.getItem("user"));
        if (!userData || !userData.id) return;

        const response = await api.get(
          `/api/approval-requests/admin/${userData.id}`
        );
        if (response.data && response.data.requests) {
          const pendingRequests = response.data.requests.filter(
            (request) => request.status === "pending"
          );
          setPendingApprovals(pendingRequests);
        }
      } catch (error) {
        console.error("Error fetching pending approvals:", error);
      } finally {
        setLoadingApprovalRequests(false);
      }
    };

    const fetchProjectStats = async () => {
      try {
        let stats = {
          active: 0,
          completed: 0,
          unassigned: 0,
          total: 0,
        };

        try {
          const response = await api.get("/projects/stats");
          if (response.data && response.data.stats) {
            stats = response.data.stats;
          }
        } catch (error) {
          console.warn(
            "Projects stats endpoint not available, fallback to default stats"
          );
        }

        setProjectStats(stats);
      } catch (error) {
        console.error("Error fetching project stats:", error);
      }
    };

    const fetchUserProjects = async () => {
      try {
        setLoading(true);
        const userData = JSON.parse(localStorage.getItem("user"));
        if (!userData || !userData.id) return;

        const response = await api.get(`/projects/user/${userData.id}`);

        if (response.data && response.data.projects) {
          const sortedProjects = response.data.projects.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          setProjects(sortedProjects);
        }
      } catch (error) {
        console.error("Error fetching user projects:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingApprovals();
    fetchProjectStats();
    fetchUserProjects();
  }, []);

  const getCurrentDate = () => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date().toLocaleDateString("en-US", options);
  };

  const user = JSON.parse(localStorage.getItem("user"));
  const name = user ? user.name : "User";

  const handleApprovalClick = (projectId) => {
    navigate(`/admin/projects/${projectId}`);
  };

  const handleProjectClick = (projectId) => {
    navigate(`/admin/projects/${projectId}`);
  };

  const quickActionLinks = [
    {
      title: "Create New Project",
      icon: <Plus size={20} />,
      path: "/admin/create-project",
      color: "#3182ce",
    },
    {
      title: "Manage Projects",
      icon: <FolderOpen size={20} />,
      path: "/admin/manage-projects",
      color: "#38a169",
    },
    {
      title: "Manage Users",
      icon: <Users size={20} />,
      path: "/admin/manage-users",
      color: "#805ad5",
    },
  ];

  return (
    <div className="dashboard-container">
      <Navbar role="admin" />

      <div className="dashboard-content">
        <div className="dashboard-header">
          <div className="header-info">
            <h1>Admin Dashboard</h1>
            <p className="date-display">{getCurrentDate()}</p>
          </div>
          <div className="user-welcome">
            <h2>Welcome back, {name}</h2>
            {pendingApprovals.length > 0 && (
              <div className="notification-badge">
                <Bell size={16} />
                <span>{pendingApprovals.length}</span>
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-grid">
          {/* Stats Cards - Important analytics first */}
          <div className="stats-overview">
            <h2 className="section-title">
              <BarChart2 size={20} className="section-icon" />
              Overview
            </h2>
            <div className="stats-cards">
              <div className="stat-card active">
                <div className="stat-icon">
                  <Activity size={24} />
                </div>
                <div className="stat-info">
                  <h3>Active Projects</h3>
                  <p>{projectStats.active}</p>
                </div>
              </div>

              <div className="stat-card completed">
                <div className="stat-icon">
                  <Bell size={24} />
                </div>
                <div className="stat-info">
                  <h3>Pending Approvals</h3>
                  <p>{pendingApprovals.length}</p>
                </div>
              </div>

              <div className="stat-card total">
                <div className="stat-icon">
                  <FolderOpen size={24} />
                </div>
                <div className="stat-info">
                  <h3>Total Projects</h3>
                  <p>{projectStats.total}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Two-column layout for main content */}
          <div className="dashboard-main">
            <div className="dashboard-column">
              {/* Pending Approvals Section - Important for admin */}
              <div className="pending-approvals-wrapper">
                <h2>
                  <Bell size={20} className="approval-bell" />
                  Document Approvals
                </h2>

                {loadingApprovals ? (
                  <div className="loading-approvals">
                    <div className="loading-spinner"></div>
                  </div>
                ) : pendingApprovals.length > 0 ? (
                  <div className="pending-approvals-list">
                    {pendingApprovals.map((approval) => (
                      <div
                        key={approval.id}
                        className="approval-card"
                        onClick={() => handleApprovalClick(approval.project_id)}
                      >
                        <div className="approval-card-header">
                          <div className="document-icon">
                            <FileText size={18} />
                          </div>
                          <span className="document-type">
                            {approval.document_type.toUpperCase()}
                          </span>
                          <span className="pending-badge">Pending</span>
                        </div>

                        <h3 className="project-title">
                          {approval.project_name || "Untitled Project"}
                        </h3>

                        <div className="approval-meta">
                          <div className="meta-item">
                            <span className="meta-label">Requested by:</span>
                            <span className="meta-value">
                              {approval.requester_name || "Unknown User"}
                            </span>
                          </div>

                          <div className="meta-item">
                            <span className="meta-label">Date:</span>
                            <span className="meta-value">
                              {new Date(approval.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        <div className="approval-card-action">
                          <span>Review Document</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-approvals">
                    <FileText size={32} />
                    <p>No pending document approvals</p>
                  </div>
                )}
              </div>

              {/* Quick Actions - Important for admin workflow */}
              <div className="quick-actions-section">
                <h2>Quick Actions</h2>
                <div className="quick-actions-grid">
                  {quickActionLinks.map((action, index) => (
                    <Link
                      to={action.path}
                      className="quick-action-card"
                      key={index}
                      style={{ borderLeftColor: action.color }}
                    >
                      <div className="action-icon" style={{ color: action.color }}>
                        {action.icon}
                      </div>
                      <span>{action.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="dashboard-column">
              {/* Recent Projects Section */}
              <div className="recent-projects-wrapper">
                <h2>My Recent Projects</h2>

                {loading ? (
                  <div className="loading-projects">
                    <div className="loading-spinner"></div>
                  </div>
                ) : projects.length > 0 ? (
                  <div className="projects-grid">
                    {projects.slice(0, 4).map((project) => (
                      <div
                        key={project.project_id}
                        className="project-card"
                        onClick={() => handleProjectClick(project.project_id)}
                      >
                        <div className="project-card-header">
                          <div className="project-icon">
                            <FolderOpen size={18} />
                          </div>
                          <span
                            className={`status-badge ${project.status || "active"}`}
                          >
                            {project.status || "Active"}
                          </span>
                        </div>

                        <h3 className="project-title">
                          {project.title || "Untitled Project"}
                        </h3>

                        <p className="project-description">
                          {project.description?.length > 70
                            ? project.description.substring(0, 70) + "..."
                            : project.description || "No description available"}
                        </p>

                        <div className="project-meta">
                          <div className="meta-item">
                            <Clock size={14} />
                            <span>
                              {new Date(project.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        <div className="project-card-action">
                          <span>View Project</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-projects">
                    <FolderOpen size={32} />
                    <p>You haven't created any projects yet</p>
                    <Link
                      to="/admin/create-project"
                      className="create-first-project-btn"
                    >
                      <Plus size={16} />
                      Create Your First Project
                    </Link>
                  </div>
                )}

                {projects.length > 0 && (
                  <div className="view-all-link">
                    <Link to="/admin/manage-projects">View all projects</Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;